import React from "react";
import { Link } from "react-router-dom";
import { Phone, Star, CheckCircle, Images, ChevronRight, CalendarDays, PhoneCall } from "lucide-react";
import { BrandIcon, getIcon } from "../utils/icons";
import { siteConfig } from "../siteConfig";
import { useQuoteModal } from "../context/QuoteModalContext";
import QuoteFormEmbed from "../components/QuoteFormEmbed";
import TrustBar from "../components/TrustBar";
import ServiceCard from "../components/ServiceCard";
import ProcessSection from "../components/ProcessSection";
import ReviewCard from "../components/ReviewCard";
import HomeAboutSection from "../components/HomeAboutSection";
import HomeReviewsSection from "../components/HomeReviewsSection";
import HomeFAQAreasSection from "../components/HomeFAQAreasSection";
import { BeforeAfterSection } from "../components/BeforeAfterSlider";
import Reveal from "../components/Reveal";

const HomePage = () => {
  const { open: openModal } = useQuoteModal();
  return (
  <>
    {/* ── HERO ── */}
    <section
      className="relative flex items-center"
      style={{
        minHeight: "100vh",
        backgroundImage: `url(${siteConfig.hero.image})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Layered dark overlay — heavier on left for text legibility */}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(105deg, rgba(0,0,0,0.82) 45%, rgba(0,0,0,0.55) 100%)" }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 pt-28 pb-20">
        <div className="grid lg:grid-cols-[1fr_520px] gap-10 items-center">

          {/* Left — copy */}
          <div>
            {/* Eyebrow badge */}
            <div
              className="hero-fade-up delay-0 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-7"
              style={{
                backgroundColor: `${siteConfig.colors.primary}22`,
                color: siteConfig.colors.primary,
                border: `1px solid ${siteConfig.colors.primary}55`,
              }}
            >
              <BrandIcon className="w-3.5 h-3.5" />
              {siteConfig.hero.eyebrow}
            </div>

            {/* Headline — base text white, highlight word in primary green */}
            <h1
              className="hero-fade-up delay-100 font-display font-black text-white leading-[1.08] mb-6"
              style={{ fontSize: "clamp(2.4rem,5.5vw,4.25rem)" }}
            >
              {siteConfig.hero.headline}{" "}
              <span style={{ color: siteConfig.colors.primary }}>
                {siteConfig.hero.headlineHighlight}
              </span>
            </h1>

            <p className="hero-fade-up delay-250 text-white/70 text-lg leading-relaxed mb-9 max-w-md">
              {siteConfig.hero.subheadline}
            </p>

            {/* CTA buttons */}
            <div className="hero-fade-up delay-400 flex flex-col sm:flex-row gap-3 mb-9">
              <button
                onClick={openModal}
                className="inline-flex items-center justify-center gap-2.5 px-7 h-14 text-base font-bold text-white rounded-xl transition-opacity hover:opacity-90"
                style={{ backgroundColor: siteConfig.colors.primary }}
              >
                <BrandIcon className="w-4 h-4" />
                {siteConfig.hero.ctaPrimaryLabel || "Get My Free Quote"}
              </button>
              <Link
                to={siteConfig.hero.ctaSecondaryHref || "/gallery"}
                className="inline-flex items-center justify-center gap-2.5 px-7 h-14 text-base font-bold text-white rounded-xl border-2 border-white/35 hover:border-white/70 transition-colors"
              >
                <Images className="w-4 h-4" />
                {siteConfig.hero.ctaSecondaryLabel || "View Our Work"}
              </Link>
            </div>

            {/* Service icon strip */}
            {(() => {
              return (
                <div
                  className="hero-fade-up delay-550 grid grid-cols-3 rounded-xl overflow-hidden mb-8"
                  style={{ backgroundColor: "rgba(0,0,0,0.40)", backdropFilter: "blur(6px)" }}
                >
                  {siteConfig.services.map((s, i) => {
                    const Icon = getIcon(s.icon);
                    const borderRight = (i % 3 !== 2) ? "1px solid rgba(255,255,255,0.12)" : "none";
                    const borderTop = i >= 3 ? "1px solid rgba(255,255,255,0.12)" : "none";
                    return (
                      <span
                        key={s.slug}
                        className="flex items-center gap-2 px-4 py-2.5 text-white/80 text-sm font-medium"
                        style={{ borderRight, borderTop }}
                      >
                        <Icon className="w-4 h-4 shrink-0" style={{ color: siteConfig.colors.primary }} />
                        {s.name}
                      </span>
                    );
                  })}
                </div>
              );
            })()}

            {/* Trust pills */}
            <div className="hero-fade-up delay-650 flex flex-wrap gap-x-5 gap-y-2">
              {["Licensed & Insured", "Free Estimates", "5-Star Rated", `Serving ${siteConfig.city}, ${siteConfig.state}`].map((t) => (
                <span key={t} className="flex items-center gap-1.5 text-white/70 text-sm">
                  <CheckCircle className="w-3.5 h-3.5 shrink-0" style={{ color: siteConfig.colors.primary }} />
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Right — quote form (desktop only) */}
          <div className="hero-slide-right delay-200 hidden lg:block">
            <QuoteFormEmbed title="Get Your Free Estimate" />
          </div>
        </div>

        {/* Mobile quote form below copy */}
        <div className="lg:hidden mt-12">
          <QuoteFormEmbed />
        </div>
      </div>
    </section>

    {/* ── TRUST BAR ── */}
    <TrustBar />

    {/* ── ABOUT ── */}
    <HomeAboutSection />

    {/* ── SERVICES ── */}
    {(() => {
      return (
        <section className="py-12 md:py-16" style={{ backgroundColor: siteConfig.colors.light }}>
          <div className="max-w-7xl mx-auto px-5 md:px-8">

            {/* Heading */}
            <Reveal as="div" className="text-center mb-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2" style={{ color: siteConfig.colors.primary }}>
                <BrandIcon className="w-3.5 h-3.5" />
                What We Do Best
              </div>
              <h2
                className="font-display font-black leading-tight mb-2"
                style={{ fontSize: "clamp(1.75rem,3.5vw,2.75rem)", color: siteConfig.colors.dark }}
              >
                Our{" "}
                <span style={{ color: siteConfig.colors.primary }}>Services</span>
              </h2>
              <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ backgroundColor: siteConfig.colors.primary }} />
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                {siteConfig.servicesSection.intro}
              </p>
            </Reveal>

            {/* Card grid — 4 columns */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {siteConfig.services.map((s, i) => {
                const Icon = getIcon(s.icon);
                return (
                  <Reveal
                    as="a"
                    key={s.slug}
                    delay={i * 90}
                    href={`/services/${s.slug}`}
                    className="group relative rounded-2xl overflow-hidden block"
                    style={{ height: "260px" }}
                  >
                    {/* Photo */}
                    <img
                      src={s.image}
                      alt={s.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />

                    {/* Dark gradient overlay */}
                    <div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.60) 55%, rgba(0,0,0,0.10) 100%)" }}
                    />

                    {/* Card content */}
                    <div className="absolute inset-x-0 bottom-0 p-3.5">
                      <div className="flex items-start gap-2.5">
                        {/* Green icon circle */}
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: siteConfig.colors.primary }}
                        >
                          <Icon className="w-4 h-4 text-white" />
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-white text-sm leading-tight mb-1">{s.name}</p>
                          <p className="text-white/65 text-xs leading-snug line-clamp-3">{s.shortDescription}</p>
                        </div>

                        {/* Arrow circle */}
                        <div
                          className="w-7 h-7 rounded-full border border-white/30 flex items-center justify-center shrink-0 mt-1 group-hover:bg-white/20 transition-colors"
                        >
                          <ChevronRight className="w-3.5 h-3.5 text-white" />
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            {/* Bottom bar */}
            <Reveal as="div" className="flex flex-col sm:flex-row items-center gap-6 bg-white rounded-2xl px-8 py-4 shadow-sm">
              {/* Left — don't see what you need */}
              <div className="flex items-center gap-4 flex-1">
                <div
                  className="w-14 h-14 rounded-full border-2 flex items-center justify-center shrink-0"
                  style={{ borderColor: siteConfig.colors.primary + "40" }}
                >
                  <CalendarDays className="w-6 h-6" style={{ color: siteConfig.colors.primary }} />
                </div>
                <div>
                  <p className="font-bold text-sm" style={{ color: siteConfig.colors.dark }}>Don't see what you need?</p>
                  <p className="text-gray-500 text-sm leading-snug">{siteConfig.servicesSection.footerTagline}</p>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px self-stretch bg-gray-200" />

              {/* Right — phone + button */}
              <div className="flex items-center gap-5 shrink-0">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: siteConfig.colors.primary + "15" }}
                  >
                    <PhoneCall className="w-5 h-5" style={{ color: siteConfig.colors.primary }} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Call us today</p>
                    <a
                      href={siteConfig.phoneHref}
                      className="font-black text-lg leading-tight"
                      style={{ color: siteConfig.colors.primary }}
                    >
                      {siteConfig.phone}
                    </a>
                    <p className="text-xs text-gray-500">or get a free quote online.</p>
                  </div>
                </div>
                <button
                  onClick={openModal}
                  className="hidden md:inline-flex items-center gap-2 px-6 h-12 text-sm font-bold text-white rounded-xl whitespace-nowrap transition-opacity hover:opacity-90"
                  style={{ backgroundColor: siteConfig.colors.primary }}
                >
                  Get My Free Quote <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>

          </div>
        </section>
      );
    })()}

    {/* ── PROCESS ── */}
    <ProcessSection />

    {/* ── SEE OUR WORK ── */}
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">

        {/* Header row */}
        <Reveal as="div" className="flex items-center gap-6 mb-8">
          <h2
            className="font-display font-black uppercase shrink-0 leading-none"
            style={{ fontSize: "clamp(1.75rem,3.5vw,2.75rem)", color: siteConfig.colors.dark }}
          >
            See Our Work
          </h2>
          <div className="flex-1 h-0.5" style={{ backgroundColor: siteConfig.colors.primary }} />
          <Link
            to="/gallery"
            className="shrink-0 inline-flex items-center gap-2 px-6 h-12 font-black text-sm uppercase tracking-widest text-white rounded-lg transition-opacity hover:opacity-85"
            style={{ backgroundColor: siteConfig.colors.dark }}
          >
            See All Photos
            <ChevronRight className="w-4 h-4" style={{ color: siteConfig.colors.primary }} />
          </Link>
        </Reveal>

        {/* Homepage preview: always limited to two rows of four on desktop. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {siteConfig.gallery.slice(0, 8).map((img, i) => (
            <Reveal key={i} delay={(i % 4) * 80} className="aspect-[4/3] rounded-xl overflow-hidden">
              <img
                src={img}
                alt={`Project ${i + 1}`}
                className="w-full h-full object-cover hover:opacity-90 transition-opacity duration-300"
              />
            </Reveal>
          ))}
        </div>

      </div>
    </section>

    {/* ── BEFORE & AFTER ── */}
    <BeforeAfterSection />

    {/* ── REVIEWS ── */}
    <HomeReviewsSection />

    {/* ── FAQ + SERVICE AREAS ── */}
    <HomeFAQAreasSection />

    {/* ── CTA ── */}
  </>
  );
};

export default HomePage;
